import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase/config';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import type { DelegationHistoryResponse } from '../types';
import axios from 'axios';
import { rateLimiter } from './rateLimiter';

// Initialize Firebase Functions
const functions = getFunctions();

// Firebase callable functions
const updateWalletDelegationHistoryFn = httpsCallable(functions, 'updateWalletDelegationHistory');
const getWalletDelegationHistoryFn = httpsCallable(functions, 'getWalletDelegationHistory');

// API key for TaoStats
const API_KEY = 'tao-1b237a3c-dcb4-468c-ad70-6b47e40e2552:6473c9a3';

// Cache for delegation data to avoid repeated API calls
const delegationCache: Record<string, {
  data: DelegationHistoryResponse['data'],
  timestamp: number
}> = {};

// Cache expiration time (5 minutes)
const CACHE_EXPIRATION = 5 * 60 * 1000;

/**
 * Triggers a Firebase function to update delegation history for a wallet
 * @param walletAddress The wallet address to update delegation history for
 */
export const updateWalletDelegationHistory = async (walletAddress: string) => {
  try {
    // For development without Firebase Functions, return mock data
    if (import.meta.env.DEV) {
      console.log('Development mode: Mocking updateWalletDelegationHistory');
      return { success: true };
    }
    
    const result = await updateWalletDelegationHistoryFn({ walletAddress });
    return result.data;
  } catch (error) {
    console.error('Error updating wallet delegation history:', error);
    // Return a mock success response in development
    if (import.meta.env.DEV) {
      return { success: true };
    }
    throw error;
  }
};

/**
 * Gets delegation history for a specific wallet and subnet from Firebase
 * @param walletAddress The wallet address
 * @param netuid The subnet ID
 */
export const getWalletDelegationHistory = async (walletAddress: string, netuid: number) => {
  try {
    // Check cache first
    const cacheKey = `${walletAddress}-${netuid}`;
    const cachedData = delegationCache[cacheKey];
    
    if (cachedData && (Date.now() - cachedData.timestamp) < CACHE_EXPIRATION) {
      console.log(`Using cached delegation data for wallet ${walletAddress} on subnet ${netuid}`);
      return {
        delegations: cachedData.data,
        costBasis: calculateCostBasis(cachedData.data)
      };
    }
    
    // For development without Firebase Functions, return mock data
    if (import.meta.env.DEV) {
      console.log(`Development mode: Mocking getWalletDelegationHistory for subnet ${netuid}`);
      return {
        delegations: [],
        costBasis: {
          totalTaoSpent: 0,
          totalTaoReceived: 0,
          totalAlphaReceived: 0,
          totalAlphaSpent: 0,
          lastUpdated: new Date().toISOString()
        }
      };
    }
    
    // First try to get from Firestore directly for faster response
    const delegationRef = doc(db, 'wallet_delegations', walletAddress, 'subnets', netuid.toString());
    const delegationDoc = await getDoc(delegationRef);
    
    if (delegationDoc.exists()) {
      const data = delegationDoc.data();
      
      // Update cache
      if (data.delegations) {
        delegationCache[cacheKey] = {
          data: data.delegations,
          timestamp: Date.now()
        };
      }
      
      return {
        delegations: data.delegations || [],
        costBasis: data.costBasis || {
          totalTaoSpent: 0,
          totalTaoReceived: 0,
          totalAlphaReceived: 0,
          totalAlphaSpent: 0,
          lastUpdated: new Date().toISOString()
        },
        lastUpdated: data.lastUpdated
      };
    }
    
    // If not found in Firestore, call the Cloud Function
    const result = await getWalletDelegationHistoryFn({ walletAddress, netuid });
    const resultData = result.data as any;
    
    // Update cache
    if (resultData.delegations) {
      delegationCache[cacheKey] = {
        data: resultData.delegations,
        timestamp: Date.now()
      };
    }
    
    return resultData;
  } catch (error) {
    console.error('Error getting wallet delegation history:', error);
    return {
      delegations: [],
      costBasis: {
        totalTaoSpent: 0,
        totalTaoReceived: 0,
        totalAlphaReceived: 0,
        totalAlphaSpent: 0,
        lastUpdated: new Date().toISOString()
      }
    };
  }
};

/**
 * Fetches delegation history directly from TaoStats API
 * @param nominator The wallet address (coldkey)
 * @param netuid The subnet ID
 */
export const fetchDelegationHistoryFromAPI = async (nominator: string, netuid: number): Promise<DelegationHistoryResponse['data']> => {
  try {
    // Check cache first
    const cacheKey = `${nominator}-${netuid}`;
    const cachedData = delegationCache[cacheKey];
    
    if (cachedData && (Date.now() - cachedData.timestamp) < CACHE_EXPIRATION) {
      console.log(`Using cached API delegation data for wallet ${nominator} on subnet ${netuid}`);
      return cachedData.data;
    }
    
    await rateLimiter.checkRateLimit();
    
    console.log(`Fetching delegation history from API for wallet ${nominator} on subnet ${netuid}`);
    
    const url = new URL('https://api.taostats.io/api/delegation/v1');
    url.searchParams.append('nominator', nominator);
    url.searchParams.append('action', 'all');
    url.searchParams.append('netuid', netuid.toString());
    
    const response = await axios.get(url.toString(), {
      headers: {
        'accept': 'application/json',
        'Authorization': API_KEY
      }
    });
    
    if (!response.data || !response.data.data) {
      console.warn(`No delegation data returned from API for wallet ${nominator} on subnet ${netuid}`);
      return [];
    }
    
    // Sort delegations by block number in descending order
    const sortedDelegations = response.data.data.sort(
      (a: any, b: any) => b.block_number - a.block_number
    );
    
    console.log(`Received ${sortedDelegations.length} delegations from API for wallet ${nominator} on subnet ${netuid}`);
    
    // Update cache
    delegationCache[cacheKey] = {
      data: sortedDelegations,
      timestamp: Date.now()
    };
    
    return sortedDelegations;
  } catch (error) {
    console.error(`Failed to fetch delegations from API for wallet ${nominator} on subnet ${netuid}:`, error);
    return [];
  }
};

/**
 * Calculate cost basis from delegation events
 */
const calculateCostBasis = (delegations: DelegationHistoryResponse['data']) => {
  return delegations.reduce((acc, delegation) => {
    const taoAmount = Number(BigInt(delegation.amount)) / 1e9;
    const alphaAmount = Number(BigInt(delegation.alpha)) / 1e9;

    if (delegation.action === 'DELEGATE') {
      acc.totalTaoSpent += taoAmount;
      acc.totalAlphaReceived += alphaAmount;
    } else {
      acc.totalTaoReceived += taoAmount;
      acc.totalAlphaSpent += alphaAmount;
    }
    return acc;
  }, {
    totalTaoSpent: 0,
    totalTaoReceived: 0,
    totalAlphaReceived: 0,
    totalAlphaSpent: 0,
    lastUpdated: new Date().toISOString()
  });
};

/**
 * Gets delegations for a specific subnet
 * This is a wrapper function to be used by components
 */
export const getDelegationsForSubnet = async (walletAddress: string, netuid: number): Promise<DelegationHistoryResponse['data']> => {
  try {
    // Check cache first
    const cacheKey = `${walletAddress}-${netuid}`;
    const cachedData = delegationCache[cacheKey];
    
    if (cachedData && (Date.now() - cachedData.timestamp) < CACHE_EXPIRATION) {
      console.log(`Using cached delegation data for wallet ${walletAddress} on subnet ${netuid}`);
      return cachedData.data;
    }
    
    // Try to get from Firebase first
    const result = await getWalletDelegationHistory(walletAddress, netuid);
    
    // If we have delegations in Firebase, use them
    if (result.delegations && result.delegations.length > 0) {
      console.log(`Using ${result.delegations.length} delegations from Firebase for subnet ${netuid}`);
      
      // Update cache
      delegationCache[cacheKey] = {
        data: result.delegations,
        timestamp: Date.now()
      };
      
      return result.delegations;
    }
    
    // Otherwise, fetch directly from the API
    console.log(`No delegations found in Firebase for subnet ${netuid}, fetching from API`);
    const apiDelegations = await fetchDelegationHistoryFromAPI(walletAddress, netuid);
    
    // Update cache
    delegationCache[cacheKey] = {
      data: apiDelegations,
      timestamp: Date.now()
    };
    
    return apiDelegations;
  } catch (error) {
    console.error(`Failed to get delegations for subnet ${netuid}:`, error);
    
    // As a fallback, try to fetch directly from the API
    try {
      return await fetchDelegationHistoryFromAPI(walletAddress, netuid);
    } catch (secondError) {
      console.error(`Fallback API call also failed for subnet ${netuid}:`, secondError);
      return [];
    }
  }
};